import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const GAP_PX = 8;

export default function InfoHover({
    text,
    ariaLabel = 'More info',
    placement = 'left', // 'left' | 'right'
    maxWidth = 320,
}) {
    const btnRef = useRef(null);
    const closeTimerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
    };

    const updatePosition = () => {
        const el = btnRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setPos({
            top: rect.bottom + GAP_PX,
            left: placement === 'right' ? rect.right : rect.left,
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, placement]);

    useEffect(() => {
        if (!open) return;

        const onScroll = () => updatePosition();
        const onResize = () => updatePosition();
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };

        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('keydown', onKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, placement]);

    useEffect(() => () => clearCloseTimer(), []);

    return (
        <>
            <IconButton
                ref={btnRef}
                type="button"
                aria-label={ariaLabel}
                onMouseEnter={() => {
                    clearCloseTimer();
                    setOpen(true);
                }}
                onMouseLeave={() => scheduleClose()}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    // prevent table-header sort handlers from firing when icon is focused
                    e.stopPropagation();
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen((v) => !v);
                    }
                }}
            >
                <Circle aria-hidden="true">i</Circle>
            </IconButton>

            {open &&
                createPortal(
                    <Popover
                        role="tooltip"
                        style={{
                            top: `${pos.top}px`,
                            left: `${pos.left}px`,
                            maxWidth: `${maxWidth}px`,
                        }}
                        data-placement={placement}
                        onMouseEnter={() => clearCloseTimer()}
                        onMouseLeave={() => scheduleClose()}
                    >
                        {text}
                    </Popover>,
                    document.body
                )}
        </>
    );
}

const IconButton = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: help;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
`;

const Circle = styled.span`
    width: 16px;
    height: 16px;
    border-radius: 999px;
    border: 1px solid #c7cfdb;
    color: #667085;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: -0.01em;

    ${IconButton}:hover & {
        border-color: #98a2b3;
        color: #475467;
    }

    ${IconButton}:focus-visible & {
        border-color: #2d70e2;
        box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.18);
    }
`;

const Popover = styled.div`
    position: fixed;
    z-index: 9999;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    box-shadow: 0 10px 25px rgba(16, 24, 40, 0.12);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.35;
    color: #2a3039;
    width: max-content;
    max-width: 320px;

    &[data-placement="right"] {
        transform: translateX(-100%);
    }
`;

